(() => {
  'use strict';

  const loaderScript = document.currentScript;
  const root = document.querySelector('[data-uth-nia-root]');
  if (!loaderScript || !root) return;

  const coreUrl = loaderScript.dataset.niaCoreUrl || '';
  const commerceUrl = loaderScript.dataset.niaCommerceUrl || '';
  let loadPromise = null;
  let modalFrame = 0;
  const suppressedControlState = new WeakMap();

  const appendScript = (src) => new Promise((resolve, reject) => {
    if (!src) {
      resolve();
      return;
    }

    const script = document.createElement('script');
    script.src = src;
    script.async = false;
    script.onload = resolve;
    script.onerror = () => reject(new Error(`Unable to load ${src}`));
    document.body.appendChild(script);
  });

  const hasVisibleExternalModal = () => Array.from(document.querySelectorAll(
    'dialog[open], [role="dialog"][aria-modal="true"], [aria-modal="true"]'
  )).some((candidate) => {
    if (root.contains(candidate)) return false;
    const style = window.getComputedStyle(candidate);
    return candidate.getClientRects().length > 0
      && style.display !== 'none'
      && style.visibility !== 'hidden';
  });

  const suppressControlForModal = (control, active) => {
    if (!(control instanceof HTMLElement)) return;

    if (active) {
      if (!suppressedControlState.has(control)) {
        suppressedControlState.set(control, {
          disabled: 'disabled' in control ? control.disabled : null,
          ariaHidden: control.getAttribute('aria-hidden'),
        });
      }
      if ('disabled' in control) control.disabled = true;
      control.setAttribute('aria-hidden', 'true');
      return;
    }

    const previous = suppressedControlState.get(control);
    if (!previous) return;
    if ('disabled' in control && previous.disabled !== null) control.disabled = previous.disabled;
    if (previous.ariaHidden === null) control.removeAttribute('aria-hidden');
    else control.setAttribute('aria-hidden', previous.ariaHidden);
    suppressedControlState.delete(control);
  };

  const syncExternalModal = () => {
    const active = hasVisibleExternalModal();
    const nextState = active ? 'true' : 'false';
    const changed = root.dataset.externalModalActive !== nextState;

    root.dataset.externalModalActive = nextState;
    root.classList.toggle('is-suppressed-by-modal', active);
    document.body.classList.toggle('uth-external-modal-open', active);

    const launcher = root.querySelector('[data-uth-nia-launcher]');
    suppressControlForModal(launcher, active);
    document.querySelectorAll('button[data-uth-nia-open]').forEach((control) => {
      suppressControlForModal(control, active);
    });

    if (changed) document.dispatchEvent(new CustomEvent('uth:nia:external-modal', { detail: { active } }));
  };

  const queueModalSync = () => {
    window.cancelAnimationFrame(modalFrame);
    modalFrame = window.requestAnimationFrame(syncExternalModal);
  };

  const load = () => {
    syncExternalModal();
    if (root.dataset.externalModalActive === 'true') return Promise.resolve(false);
    if (loadPromise) return loadPromise;

    root.dataset.niaLoading = 'true';
    loadPromise = appendScript(coreUrl)
      .then(() => appendScript(commerceUrl))
      .then(() => {
        root.dataset.niaLoaded = 'true';
        return true;
      })
      .catch((error) => {
        root.dataset.niaLoadFailed = 'true';
        window.console.error('[UTH Hoodie]', error);
        return false;
      })
      .finally(() => {
        delete root.dataset.niaLoading;
      });

    return loadPromise;
  };

  window.UTHNiaLoad = load;
  syncExternalModal();

  if ('MutationObserver' in window) {
    const observer = new MutationObserver(queueModalSync);
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['open', 'hidden', 'aria-hidden', 'aria-modal', 'class', 'style'],
    });
  }

  document.addEventListener('click', (event) => {
    const target = event.target.closest('[data-uth-nia-launcher], [data-uth-nia-open], [data-uth-nia-fit], [data-uth-nia-prompt]');
    if (!target || root.dataset.niaLoaded === 'true') return;

    event.preventDefault();
    event.stopImmediatePropagation();
    load().then((ready) => {
      if (ready && root.dataset.niaLoaded === 'true' && target.isConnected) target.click();
    });
  }, true);

  ['pointerover', 'focusin', 'touchstart'].forEach((eventName) => {
    document.addEventListener(eventName, (event) => {
      if (event.target.closest('[data-uth-nia-launcher], [data-uth-nia-open], [data-uth-nia-fit], [data-uth-nia-prompt]')) {
        load();
      }
    }, { capture: true, passive: true });
  });
})();
